import { PolicyLayout } from '../components/PolicyLayout';
import { ContactBlock } from '../components/ContactBlock';

const SECTIONS = [
  { id: 'commitment', title: 'Our Commitment' },
  { id: 'scope', title: 'Scope' },
  { id: 'definitions', title: 'Key Definitions' },
  { id: 'principles', title: 'Safeguarding Principles' },
  { id: 'provider-requirements', title: 'Provider Requirements' },
  { id: 'platform-measures', title: 'Platform Safety Measures' },
  { id: 'recognising-abuse', title: 'Recognising Abuse and Neglect' },
  { id: 'reporting', title: 'Reporting Concerns' },
  { id: 'user-responsibilities', title: 'User Responsibilities' },
  { id: 'emergency', title: 'Emergency Situations' },
  { id: 'training', title: 'Training and Awareness' },
  { id: 'review', title: 'Policy Review' },
  { id: 'contact', title: 'Contact and Reporting' },
];

export const SafeguardingPolicy = () => {
  return (
    <PolicyLayout title="Safeguarding Policy" lastUpdated="22 April 2026" sections={SECTIONS}>
      <section id="commitment">
        <h2>Our Commitment</h2>
        <ul>
          <li>
            NursingCareBooking.com is committed to safeguarding and promoting the welfare of all
            individuals who use our Platform, particularly vulnerable adults and children who receive
            care services arranged through our marketplace.
          </li>
          <li>
            We recognise our responsibility to take all reasonable steps to promote safe practice
            and to protect individuals from harm, abuse, and exploitation.
          </li>
          <li>
            This policy outlines our approach to safeguarding and the responsibilities of all
            parties involved in care delivered through our Platform.
          </li>
          <li>
            We work in alignment with the Care Act 2014, the Mental Capacity Act 2005, and the Care
            Quality Commission (CQC) fundamental standards.
          </li>
        </ul>
      </section>

      <section id="scope">
        <h2>Scope</h2>
        <p>This policy applies to:</p>
        <ul>
          <li>All Care Providers registered on the Platform</li>
          <li>All Service Users and their care recipients</li>
          <li>NursingCareBooking.com staff and administrators</li>
          <li>Any person who comes into contact with care services arranged through the Platform</li>
        </ul>
        <p>This policy covers:</p>
        <ul>
          <li>The safeguarding of adults at risk of abuse or neglect</li>
          <li>The safeguarding of children who may be care recipients</li>
          <li>The prevention of harm and abuse in care settings</li>
          <li>The reporting and management of safeguarding concerns</li>
        </ul>
      </section>

      <section id="definitions">
        <h2>Key Definitions</h2>
        <ul>
          <li>
            <strong>Safeguarding</strong>: Protecting an individual's right to live in safety, free
            from abuse and neglect. It involves people and organisations working together to prevent
            and stop both the risks and experience of abuse or neglect.
          </li>
          <li>
            <strong>Adult at Risk</strong>: A person aged 18 or over who has needs for care and
            support, is experiencing or is at risk of abuse or neglect, and as a result of those
            care and support needs is unable to protect themselves against the abuse or neglect.
          </li>
          <li>
            <strong>Abuse</strong>: A violation of an individual's human or civil rights by another
            person or persons. This can take many forms including physical, sexual, psychological,
            financial, organisational, discriminatory, neglect, and self-neglect.
          </li>
          <li>
            <strong>Neglect</strong>: The failure of a person who has responsibility for the care of
            a vulnerable person to provide the amount and type of care that a reasonable person
            would be expected to provide.
          </li>
          <li>
            <strong>Disclosure and Barring Service (DBS)</strong>: The government service that
            processes criminal record checks for organisations.
          </li>
        </ul>
      </section>

      <section id="principles">
        <h2>Safeguarding Principles</h2>
        <p>
          Our approach is guided by the six principles of adult safeguarding from the Care Act 2014:
        </p>
        <ul>
          <li>
            <strong>Empowerment</strong>: People are supported and encouraged to make their own
            decisions and give informed consent.
          </li>
          <li>
            <strong>Prevention</strong>: It is better to take action before harm occurs.
          </li>
          <li>
            <strong>Proportionality</strong>: The least intrusive response appropriate to the risk
            presented.
          </li>
          <li>
            <strong>Protection</strong>: Support and representation for those in greatest need.
          </li>
          <li>
            <strong>Partnership</strong>: Local solutions through services working with their
            communities.
          </li>
          <li>
            <strong>Accountability</strong>: Accountability and transparency in safeguarding
            practice.
          </li>
        </ul>
      </section>

      <section id="provider-requirements">
        <h2>Provider Requirements</h2>
        <p>
          Care Providers registered on NursingCareBooking.com are expected to:
        </p>
        <ul>
          <li>Hold a valid DBS check (Enhanced, where appropriate) and keep it up to date</li>
          <li>Maintain CQC registration where required by law</li>
          <li>
            Have completed safeguarding training appropriate to their role and level of
            responsibility
          </li>
          <li>
            Understand and comply with the Mental Capacity Act 2005 when caring for individuals who
            may lack capacity
          </li>
          <li>Maintain professional indemnity insurance and public liability insurance</li>
          <li>
            Follow their own organisation's safeguarding policies and procedures (for agency
            providers)
          </li>
          <li>
            Report any safeguarding concerns immediately to the appropriate authorities and to
            NursingCareBooking.com
          </li>
          <li>Maintain accurate records of care provided</li>
          <li>Treat all individuals with dignity, respect, and compassion</li>
          <li>Never engage in any form of abuse, neglect, or exploitation</li>
          <li>Cooperate fully with any safeguarding investigations</li>
        </ul>
        <ul>
          <li>
            Providers who list "DBS Checked" and "CQC Registered" in their credentials on the
            Platform are representing that these are current and valid. Misrepresentation of
            credentials is grounds for immediate account termination.
          </li>
        </ul>
      </section>

      <section id="platform-measures">
        <h2>Platform Safety Measures</h2>
        <p>
          NursingCareBooking.com implements the following measures to support safeguarding:
        </p>
        <ul>
          <li>
            <strong>Provider Verification</strong>: We verify Care Provider credentials including
            CQC registration status. Verified providers display a verification badge on their
            profile.
          </li>
          <li>
            <strong>Provider Blocking</strong>: We have the ability to immediately block Care
            Providers from the Platform if safeguarding concerns arise. Blocked providers are
            removed from search results and cannot receive new bookings.
          </li>
          <li>
            <strong>Review Moderation</strong>: All reviews are moderated to identify potential
            safeguarding concerns. Reviews can be flagged for further investigation.
          </li>
          <li>
            <strong>Reporting Mechanisms</strong>: Users can flag concerns through the Platform's
            support channels and review system.
          </li>
          <li>
            <strong>Information Sharing</strong>: Relevant care recipient information (medical
            conditions, mobility, medications, emergency contacts) is shared with providers at
            booking time to support safe care delivery.
          </li>
          <li>
            <strong>Admin Oversight</strong>: Our administrative team monitors platform activity and
            can take swift action when concerns are raised.
          </li>
          <li>
            <strong>Account Deletion Requests</strong>: We review all account deletion requests to
            identify any potential safeguarding issues before processing.
          </li>
        </ul>
      </section>

      <section id="recognising-abuse">
        <h2>Recognising Abuse and Neglect</h2>
        <p>All users should be aware of the signs of abuse and neglect:</p>
        <ul>
          <li>
            <strong>Physical Abuse</strong>: Unexplained injuries, bruising, burns, or marks.
            Reluctance to be touched. Changes in behaviour after care visits.
          </li>
          <li>
            <strong>Emotional/Psychological Abuse</strong>: Withdrawal, fearfulness, changes in
            mood, low self-esteem, agitation, or confusion.
          </li>
          <li>
            <strong>Financial Abuse</strong>: Unexplained loss of money or possessions, sudden
            changes in financial situation, unusual bank account activity.
          </li>
          <li>
            <strong>Neglect</strong>: Poor personal hygiene, malnutrition, dehydration, untreated
            medical conditions, unsuitable living conditions, missed medications.
          </li>
          <li>
            <strong>Sexual Abuse</strong>: Unexplained bruising in genital areas, sexually
            transmitted infections, inappropriate sexual behaviour, withdrawal or distress.
          </li>
          <li>
            <strong>Organisational Abuse</strong>: Rigid routines, inadequate staffing, lack of
            person-centred care, poor complaint procedures.
          </li>
          <li>
            <strong>Discriminatory Abuse</strong>: Harassment, slurs, or unfair treatment based on
            age, disability, gender, race, religion, sexuality, or other protected characteristics.
          </li>
          <li>
            <strong>Self-Neglect</strong>: Neglecting personal hygiene, health, or surroundings.
            Hoarding. Refusing essential care or services.
          </li>
        </ul>
        <ul>
          <li>This list is not exhaustive. If something does not feel right, report it.</li>
        </ul>
      </section>

      <section id="reporting">
        <h2>Reporting Concerns</h2>
        <p>If you have a safeguarding concern:</p>
        <ul>
          <li>
            <strong>Immediate danger</strong>: Call 999 immediately.
          </li>
          <li>
            <strong>Non-emergency concerns</strong>: Contact your local adult safeguarding team or
            the police non-emergency line (101).
          </li>
          <li>
            <strong>Report to us</strong>: Email{' '}
            <a href="mailto:Info@nursingcarebooking.com">Info@nursingcarebooking.com</a> or
            call <a href="tel:+447462365503">+44 7462 365503</a> or{' '}
            <a href="https://wa.me/447462365503" target="_blank" rel="noopener noreferrer">WhatsApp us</a>.
          </li>
          <li>
            <strong>CQC</strong>: Report concerns about regulated care services to the CQC at{' '}
            <a href="https://www.cqc.org.uk" target="_blank" rel="noopener noreferrer">
              cqc.org.uk
            </a>
            .
          </li>
        </ul>
        <p>When reporting to us, include:</p>
        <ul>
          <li>The nature of the concern</li>
          <li>Names of individuals involved (where known)</li>
          <li>Dates, times, and locations</li>
          <li>Any evidence or observations</li>
          <li>Any immediate action taken</li>
        </ul>
        <ul>
          <li>All reports will be treated confidentially and investigated promptly.</li>
          <li>
            You will not be penalised for raising a genuine safeguarding concern in good faith.
          </li>
        </ul>
      </section>

      <section id="user-responsibilities">
        <h2>User Responsibilities</h2>
        <h3>Service Users</h3>
        <ul>
          <li>Provide accurate health and medical information</li>
          <li>Report any concerns about care quality or provider behaviour promptly</li>
          <li>Ensure the care environment is safe and accessible</li>
          <li>Keep emergency contact information current</li>
        </ul>
        <h3>Care Providers</h3>
        <ul>
          <li>Follow professional codes of conduct</li>
          <li>Report safeguarding concerns immediately</li>
          <li>Maintain appropriate professional boundaries</li>
          <li>Do not accept gifts or financial benefits from vulnerable individuals</li>
          <li>Document and report any incidents or near-misses</li>
          <li>Cooperate with investigations</li>
        </ul>
        <h3>All Users</h3>
        <ul>
          <li>Treat others with dignity and respect</li>
          <li>Do not engage in any form of harassment, discrimination, or exploitation</li>
          <li>Report concerns promptly rather than assuming someone else will</li>
        </ul>
      </section>

      <section id="emergency">
        <h2>Emergency Situations</h2>
        <p>
          In an emergency where there is immediate risk to life or safety:
        </p>
        <ul>
          <li>Call 999 for emergency services</li>
          <li>Administer first aid if trained and safe to do so</li>
          <li>Do not move the person unless they are in immediate danger</li>
          <li>Preserve any evidence if abuse is suspected</li>
          <li>
            Contact NursingCareBooking.com support as soon as practicable after ensuring safety
          </li>
        </ul>
        <ul>
          <li>
            For non-life-threatening emergencies, contact the relevant local authority safeguarding
            team or the police non-emergency line (101).
          </li>
        </ul>
      </section>

      <section id="training">
        <h2>Training and Awareness</h2>
        <p>
          We encourage all Care Providers to maintain up-to-date safeguarding training. Providers
          should be trained in:
        </p>
        <ul>
          <li>Recognising the signs of abuse and neglect</li>
          <li>Knowing how and when to report concerns</li>
          <li>Understanding the Mental Capacity Act</li>
          <li>Safe working practices including lone working</li>
          <li>Record-keeping and documentation</li>
        </ul>
        <p>Resources for safeguarding training:</p>
        <ul>
          <li>
            Social Care Institute for Excellence (SCIE) at{' '}
            <a href="https://www.scie.org.uk" target="_blank" rel="noopener noreferrer">
              scie.org.uk
            </a>
          </li>
          <li>
            Skills for Care at{' '}
            <a href="https://www.skillsforcare.org.uk" target="_blank" rel="noopener noreferrer">
              skillsforcare.org.uk
            </a>
          </li>
          <li>Your local authority safeguarding adults board</li>
        </ul>
        <ul>
          <li>
            We may provide information and resources on safeguarding through the Platform from time
            to time.
          </li>
        </ul>
      </section>

      <section id="review">
        <h2>Policy Review</h2>
        <ul>
          <li>
            This Safeguarding Policy is reviewed annually and updated as necessary to reflect
            changes in legislation, best practice, or our operations.
          </li>
          <li>
            Any significant incidents or concerns may trigger an immediate review of this policy.
          </li>
          <li>We welcome feedback on this policy from all stakeholders.</li>
        </ul>
      </section>

      <section id="contact">
        <h2>Contact and Reporting</h2>
        <ContactBlock
          introText="To report a safeguarding concern or for questions about this policy:"
          extraContent={
            <>
              <p className="font-medium text-gray-700 mt-4 mb-2">Emergency and external contacts:</p>
              <ul>
                <li>Emergency services: 999</li>
                <li>Police non-emergency: 101</li>
                <li>
                  Care Quality Commission:{' '}
                  <a href="https://www.cqc.org.uk" target="_blank" rel="noopener noreferrer">
                    cqc.org.uk
                  </a>
                </li>
                <li>
                  Local authority safeguarding team (search for your local council's safeguarding
                  contact)
                </li>
              </ul>
              <p className="font-semibold text-gray-800 mt-3">
                Remember: safeguarding is everyone's responsibility. If you see something, say
                something.
              </p>
            </>
          }
        />
      </section>
    </PolicyLayout>
  );
};
